import { useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { DemoSize } from '../types.js'
import { ADMIN_API_BASE_URL, APPLICATION_NAME, AUTHORIZATION_SERVER_ISSUER, GRANTABLE_SCOPES } from '../../core/index.js'
import { defaultKid, onboardClient, OnboardingError, type OnboardingInput } from './onboardClient.js'
import { OnboardingOutcome, type OnboardingRunState } from './OnboardingOutcome.js'
import { CustomDemoFields } from './CustomDemoFields.js'
import { DEMO_REQUIRED_SCOPES, buildDemoOnboardingDetails } from '../demo/demoData.js'
import {
  DEFAULT_DEMO_SCENARIO,
  DEFAULT_DEMO_SIZE,
  DEMO_SCENARIO_OPTIONS,
  DEFAULT_MONTH_SPAN,
  DEMO_SIZE_OPTIONS,
  MONTH_SPAN_LIMITS,
  demoPlanFor,
  describeDemoPlan,
  customDemoProblems,
  customMaxMonthSpan,
  DEFAULT_CUSTOM_DEMO,
  isValidMonthSpan,
  maxMonthSpanFor,
  type DemoChoice,
} from '../demo/demoPlan.js'
import { isBlankPlan } from '../demo/demoRules.js'
import { Rocket } from 'lucide-react'
import { Button } from '../../core/index.js'
import type { ComponentProps } from 'react'
import type { OnboardingAdminApi } from './onboardClient.js'
import { PhoneInput } from '../../core/index.js'

/** The admin calls this screen makes itself, and what the screens it opens need. */
type AdminApi = OnboardingAdminApi & ComponentProps<typeof OnboardingOutcome>['api']


type Environment = OnboardingInput['environment']

interface FormState {
  organizationName: string
  contactEmail: string
  contactPhone: string
  operatorFirstName: string
  operatorLastName: string
  environment: Environment
  scopes: string[]
  /** The demo size and span when Demo client is on, null when it is off. */
  demo: DemoChoice | null
  /** Null until the admin edits it - until then it tracks the organization name and environment. */
  kidOverride: string | null
}

const EMPTY_FORM: FormState = {
  organizationName: '',
  contactEmail: '',
  contactPhone: '',
  operatorFirstName: '',
  operatorLastName: '',
  environment: 'production',
  scopes: ['events:write'],
  demo: null,
  kidOverride: null,
}

const INPUT_CLASS = 'w-full border border-marine/25 px-3 py-2 text-sm text-ink focus:outline-none focus:border-marine'

function Labeled({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && <p className="text-[11px] text-ink-mid mt-1">{hint}</p>}
    </div>
  )
}

interface DemoChoiceFieldsProps {
  value: DemoChoice
  onChange: (choice: DemoChoice) => void
  /** The sizes on offer: all of them for an admin, all but the largest for a sales login. */
  sizes: typeof DEMO_SIZE_OPTIONS
  /** A sales login: a custom demo kept within a standard demo's reach. */
  forSales: boolean
}

/** The longest history the choice may have: a custom demo's follows its facilities. */
function maxSpanOf(choice: DemoChoice): number {
  return choice.size === 'custom' ? customMaxMonthSpan(choice.custom ?? DEFAULT_CUSTOM_DEMO) : maxMonthSpanFor(choice.size)
}

/** Whether the choice can be built: its span in range and, for a custom demo, every setting. */
function isDemoChoiceValid(choice: DemoChoice, forSales: boolean): boolean {
  if (isBlankPlan({ size: choice.size })) {
    return true
  }
  const spanOk = isValidMonthSpan(choice.monthSpan) && choice.monthSpan <= maxSpanOf(choice)
  return spanOk && (choice.size !== 'custom' || customDemoProblems(choice.custom ?? DEFAULT_CUSTOM_DEMO, choice.monthSpan, forSales).length === 0)
}

/** The API refuses a sales login the Large demo, so the form does not offer it. */
const SALES_DEMO_SIZES = DEMO_SIZE_OPTIONS.filter(({ size }) => size !== 'large')

/** Which dataset the demo gets and how many months its outcomes span: a radio per size, each spelling out what it sets up for the span chosen. */
function DemoChoiceFields({ value, onChange, sizes, forSales }: DemoChoiceFieldsProps) {
  const maxSpan = maxSpanOf(value)
  const spanIsValid = isValidMonthSpan(value.monthSpan) && value.monthSpan <= maxSpan
  // While the field is being retyped it passes through values that are not a span (empty, then one digit of two).
  // The descriptions and the estimate keep to the last span that was, so the text does not vanish and jump mid-edit.
  const lastValidSpan = useRef(spanIsValid ? value.monthSpan : DEFAULT_MONTH_SPAN)
  if (spanIsValid) {
    lastValidSpan.current = value.monthSpan
  }
  const describedSpan = lastValidSpan.current
  // Building a plan (and, for the estimate, every outcome) is cheap, but not something to repeat on every keystroke elsewhere in the form.
  const descriptions = useMemo(
    () =>
      Object.fromEntries(
        DEMO_SIZE_OPTIONS.map(({ size }) => {
          // A custom demo is described from its settings; while one is out of range there is nothing to describe yet.
          try {
            return [size, describeDemoPlan(demoPlanFor({ size, monthSpan: Math.min(describedSpan, maxSpanOf({ size, monthSpan: describedSpan, custom: value.custom })), custom: value.custom }))]
          } catch {
            return [size, '']
          }
        }),
      ) as Record<DemoSize, string>,
    [describedSpan, value.custom],
  )
  const isBlank = isBlankPlan({ size: value.size })
  return (
    <div className="mt-4 pl-7 space-y-4">
      <div className="max-w-xs">
        <Labeled
          label="Months of history"
          htmlFor="onboard-demo-months"
          hint={isBlank ? 'A blank demo has no history to report.' : `This month and the months before it, ${MONTH_SPAN_LIMITS.min} to ${maxSpan}. Every month is lived as a working facility would have: its census, its seats published from the staffing plan, bid, built at the close, released and worked, and every month before this one closed - written on the API in the background, a few minutes for a long span.`}
        >
          <input
            id="onboard-demo-months"
            type="number"
            inputMode="numeric"
            className={INPUT_CLASS}
            value={Number.isNaN(value.monthSpan) ? '' : value.monthSpan}
            min={MONTH_SPAN_LIMITS.min}
            max={maxSpan}
            step={1}
            required
            disabled={isBlank}
            onChange={(event) => onChange({ ...value, monthSpan: event.target.valueAsNumber })}
          />
        </Labeled>
        {!spanIsValid && !isBlank && (
          <p className="text-[11px] text-coral font-bold mt-1">
            Enter a whole number from {MONTH_SPAN_LIMITS.min} to {maxSpan}.
          </p>
        )}
      </div>
      <fieldset>
        <legend className="block text-xs font-bold text-ink-mid mb-2">Demo size</legend>
        <div className="space-y-3">
          {sizes.map(({ size, label, summary }) => (
            <label key={size} className="flex items-start gap-2 text-sm text-ink cursor-pointer">
              <input type="radio" name="demo-size" className="mt-0.5" value={size} checked={value.size === size} onChange={() => onChange({ ...value, size })} />
              <span>
                <span className="font-bold">{label}</span>
                <span className="block text-[11px] text-ink-mid">
                  {summary} {descriptions[size]}
                </span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      {value.size === 'custom' && <CustomDemoFields value={value.custom ?? DEFAULT_CUSTOM_DEMO} onChange={(custom) => onChange({ ...value, custom })} monthSpan={value.monthSpan} forSales={forSales} />}
      {!isBlank && (
        <fieldset>
          <legend className="block text-xs font-bold text-ink-mid mb-2">Scenario</legend>
          <p className="text-[11px] text-ink-mid mb-2">
            {value.size === 'large' ? 'The story the history tells. A large demo deals its twelve facilities with history one of each in turn, starting with this one.' : 'The story the history tells: the same realistic facility, with the dials set for it.'}
          </p>
          <div className="space-y-2">
            {DEMO_SCENARIO_OPTIONS.map(({ scenario, label, summary }) => (
              <label key={scenario} className="flex items-start gap-2 text-sm text-ink cursor-pointer">
                <input type="radio" name="demo-scenario" className="mt-0.5" value={scenario} checked={(value.scenario ?? DEFAULT_DEMO_SCENARIO) === scenario} onChange={() => onChange({ ...value, scenario })} />
                <span>
                  <span className="font-bold">{label}</span>
                  <span className="block text-[11px] text-ink-mid">{summary}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </div>
  )
}

/** A fresh form with Demo on and its fake organization filled in: what turning the switch on gives, and where a demo-only form starts. */
function demoForm(scopes: readonly string[] = EMPTY_FORM.scopes): FormState {
  return {
    ...EMPTY_FORM,
    ...buildDemoOnboardingDetails(crypto.randomUUID().slice(0, 4)),
    scopes: [...new Set([...scopes, ...DEMO_REQUIRED_SCOPES])],
    demo: { size: DEFAULT_DEMO_SIZE, monthSpan: DEFAULT_MONTH_SPAN, scenario: DEFAULT_DEMO_SCENARIO },
  }
}

interface OnboardClientPageProps {
  api: AdminApi
  /**
   * For a sales login: the form only ever makes a demo. Demo is on and
   * cannot be turned off, and the largest size is not offered - the API
   * enforces both, so this is the form agreeing with it, not the guard.
   */
  demoOnly?: boolean
}

export function OnboardClientPage({ api, demoOnly = false }: OnboardClientPageProps) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<FormState>(() => (demoOnly ? demoForm() : EMPTY_FORM))
  const [run, setRun] = useState<OnboardingRunState | null>(null)

  const kid = form.kidOverride ?? defaultKid(form.organizationName, form.environment)

  function update(changes: Partial<FormState>): void {
    setForm((current) => ({ ...current, ...changes }))
  }

  // Turning Demo on fills the form with a fake organization and contact, ready to review and submit - it never
  // submits by itself. The kid is left to follow the filled-in name, and the scopes gain the ones a demo's own
  // setup needs. Turning it off clears the form, so fake details are never left behind for a real client.
  function toggleDemo(): void {
    if (demoOnly) {
      return
    }
    setForm((current) => (current.demo ? EMPTY_FORM : demoForm(current.scopes)))
  }

  function toggleScope(scope: string): void {
    setForm((current) => ({
      ...current,
      scopes: current.scopes.includes(scope) ? current.scopes.filter((existing) => existing !== scope) : [...current.scopes, scope],
    }))
  }

  const isDemoValid = form.demo === null || isDemoChoiceValid(form.demo, demoOnly)

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (form.scopes.length === 0 || !kid.trim() || !isDemoValid) {
      return
    }
    // A blank demo carries the default span so the plan is always valid, whatever was typed before switching to it.
    const demo: DemoChoice | null = form.demo && isBlankPlan({ size: form.demo.size }) ? { ...form.demo, monthSpan: DEFAULT_MONTH_SPAN } : form.demo
    const input: OnboardingInput = {
      environment: form.environment,
      organizationName: form.organizationName.trim(),
      applicationName: APPLICATION_NAME,
      contactEmail: form.contactEmail.trim(),
      contactPhone: form.contactPhone.trim(),
      operatorFirstName: form.operatorFirstName.trim(),
      operatorLastName: form.operatorLastName.trim(),
      scopes: form.scopes,
      kid: kid.trim(),
      demo,
    }

    setRun({ progress: { completed: [], credentials: [] }, running: 'register', failure: null, isFinished: false, demo })
    try {
      const progress = await onboardClient(
        api,
        input,
        AUTHORIZATION_SERVER_ISSUER,
        (latest, running) => setRun({ progress: latest, running, failure: null, isFinished: false, demo }),
        ADMIN_API_BASE_URL ?? '',
      )
      setRun({ progress, running: null, failure: null, isFinished: true, demo })
    } catch (error) {
      if (error instanceof OnboardingError) {
        setRun({ progress: error.progress, running: null, failure: { step: error.failedStep, message: error.message }, isFinished: false, demo })
      } else {
        setRun({
          progress: { completed: [], credentials: [] },
          running: null,
          failure: { step: 'register', message: error instanceof Error ? error.message : 'Unexpected error' },
          isFinished: false,
          demo,
        })
      }
    } finally {
      void queryClient.invalidateQueries({ queryKey: ['admin'] })
    }
  }

  if (run) {
    return (
      <div className="max-w-3xl mx-auto px-7 py-6">
        <OnboardingOutcome
          api={api}
          run={run}
          onStartAnother={() => {
            setForm(demoOnly ? demoForm() : EMPTY_FORM)
            setRun(null)
          }}
        />
      </div>
    )
  }

  const canSubmit = form.scopes.length > 0 && kid.trim().length > 0 && isDemoValid

  return (
    <div className="max-w-3xl mx-auto px-7 py-6">
      <form onSubmit={(event) => void handleSubmit(event)} className="bg-white border border-marine/15 p-6 space-y-5">
        <p className="text-xs text-ink-mid">
          Registers the organization, generates and activates its API key, and creates its first operator login - all in one step. This
          creates a real client on the API this dashboard is connected to.
        </p>

        <div className={`border p-4 ${form.demo ? 'border-coral bg-coral-lt/40' : 'border-marine/15'}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              role="switch"
              aria-checked={form.demo !== null}
              className="mt-0.5"
              checked={form.demo !== null}
              disabled={demoOnly}
              onChange={toggleDemo}
            />
            <span>
              <span className="block text-sm font-bold text-ink">Demo client</span>
              <span className="block text-[11px] text-ink-mid">
                Does everything below, then fills the client with fake data so it can be shown straight away. You get every username and
                password at the end. A demo client is the only kind that can be deleted later.
              </span>
            </span>
          </label>
          {form.demo && <DemoChoiceFields value={form.demo} onChange={(demo) => update({ demo })} sizes={demoOnly ? SALES_DEMO_SIZES : DEMO_SIZE_OPTIONS} forSales={demoOnly} />}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Labeled label="Organization name" htmlFor="onboard-org">
            <input
              id="onboard-org"
              className={INPUT_CLASS}
              value={form.organizationName}
              onChange={(event) => update({ organizationName: event.target.value })}
              required
              autoFocus
            />
          </Labeled>
          <div>
            <p className="block text-xs font-bold text-ink-mid mb-1">Application name</p>
            <p className="py-2 text-sm text-ink">{APPLICATION_NAME}</p>
            <p className="text-[11px] text-ink-mid mt-1">The only product, so there is nothing to choose.</p>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Labeled
            label="Contact email"
            htmlFor="onboard-email"
            hint="Also becomes the username of the operator login created for this client."
          >
            <input
              id="onboard-email"
              type="email"
              className={INPUT_CLASS}
              value={form.contactEmail}
              onChange={(event) => update({ contactEmail: event.target.value })}
              required
            />
          </Labeled>
          <Labeled label="Contact phone" htmlFor="onboard-phone" hint="Optional.">
            <PhoneInput id="onboard-phone" className={INPUT_CLASS} value={form.contactPhone} onChange={(contactPhone) => update({ contactPhone })} autoComplete="off" />
          </Labeled>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Labeled label="Contact first name" htmlFor="onboard-first-name" hint="The contact is also the first operator.">
            <input
              id="onboard-first-name"
              className={INPUT_CLASS}
              value={form.operatorFirstName}
              onChange={(event) => update({ operatorFirstName: event.target.value })}
              required
            />
          </Labeled>
          <Labeled label="Contact last name" htmlFor="onboard-last-name">
            <input
              id="onboard-last-name"
              className={INPUT_CLASS}
              value={form.operatorLastName}
              onChange={(event) => update({ operatorLastName: event.target.value })}
              required
            />
          </Labeled>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Labeled label="Environment" htmlFor="onboard-env">
            <select
              id="onboard-env"
              className={INPUT_CLASS}
              value={form.environment}
              onChange={(event) => update({ environment: event.target.value as Environment })}
              required
            >
              <option value="production">Production</option>
              <option value="sandbox">Sandbox</option>
            </select>
          </Labeled>
          <Labeled label="Key ID (kid)" htmlFor="onboard-kid" hint="Must be unique across all clients.">
            <input
              id="onboard-kid"
              className={`${INPUT_CLASS} font-mono`}
              value={kid}
              onChange={(event) => update({ kidOverride: event.target.value })}
              required
            />
          </Labeled>
        </div>

        <fieldset>
          <legend className="block text-xs font-bold text-ink-mid mb-2">Scopes</legend>
          <div className="space-y-2">
            {GRANTABLE_SCOPES.map(({ scope, description }) => (
              <label key={scope} className="flex items-start gap-2 text-sm text-ink">
                <input type="checkbox" className="mt-0.5" checked={form.scopes.includes(scope)} onChange={() => toggleScope(scope)} />
                <span>
                  <span className="font-mono text-xs font-bold">{scope}</span>
                  <span className="block text-[11px] text-ink-mid">{description}</span>
                </span>
              </label>
            ))}
          </div>
          {form.scopes.length === 0 && <p className="text-[11px] text-coral font-bold mt-2">Select at least one scope.</p>}
        </fieldset>

        <Button type="submit" icon={Rocket} size="lg" disabled={!canSubmit}>
          {form.demo ? 'Create demo client' : 'Onboard client'}
        </Button>
      </form>
    </div>
  )
}
